import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import VitalityDuplicatedTable from "../../stories/Tables/VitalityDuplicatedTable";
import PageLayout from "../../components/Layout/PageLayout";
import ExitModal from "../../stories/Modals/ExitModal";
import ConfirmSubmissionModal from "../../stories/Modals/ConfirmSubmissionModal";
import {
  vitalitySearchFormFields,
  getScreeningQuestions,
  bodyMeasurementsColumns,
  bloodPressureColumns,
  lipidProfileColumns,
  glucoseColumns,
  getEnabledSections,
  MEASUREMENT_SECTIONS,
} from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import Notes from "../../stories/Forms/Notes";
import { Button } from "../../stories/Buttons/Button";
import ExitConsultationButton from "../../stories/Buttons/ExitConsultationButton";
import { useLocation, useNavigate } from "react-router";
import VHConfirmationPopup from "../../stories/Modals/VHConfirmationPopup";
import { useConsultationDraft } from "../../hooks/useConsultationDraft";
import Spinner from "react-bootstrap/Spinner";
import { addIds, getRangeError, formatDtAge } from "../../utils/utils";
import DetailCard from "../../stories/Cards/DetailCard";
import InfoDefaultIcon from "../../assets/icons/Information-default.svg";
import WarningIcon from "../../assets/icons/Warning-default.svg";
import EditIcon from "../../assets/icons/Vitality-edit.svg";
import {
  resultOutcomeGet,
  consultationSubmitPost,
  CLASSIFICATION_MAP,
  PARAMETER_LABEL_MAP,
  patientDetailsGet,
} from "../../utils/constants";
import useApi from "../../services/useAPI";
import { XCircleFill } from "react-bootstrap-icons";

const CLINICAL_SCREENING_EDIT_ROUTE = "/clinical-screening";
const CLINICAL_MEASUREMENTS_EDIT_ROUTE = "/clinical-measurement";
const ALLOWED_STAGES = ["clinical-measurement"];

const getClassificationModifier = (result) => {
  if (!result || result === "-" || result === " ") {
    return null;
  }

  const normalized = String(result).toLowerCase().trim();

  if (CLASSIFICATION_MAP[normalized]) {
    return CLASSIFICATION_MAP[normalized];
  }

  const original = String(result).trim();
  return {
    className: "classification-unknown",
    label: original.charAt(0).toUpperCase() + original.slice(1),
  };
};

const renderClassification = (result) => {
  if (!result || result === "-") {
    return "-";
  }

  const classification = getClassificationModifier(result);

  if (!classification) {
    return result;
  }

  return (
    <span className={`classification-chip ${classification.className}`}>
      {classification.label}
    </span>
  );
};

const FIELD_VALIDATION_MAP = {
  bmi: { columns: bodyMeasurementsColumns, dataField: "bmi", section: MEASUREMENT_SECTIONS.BODY_MEASUREMENTS },
  waistHeightRatio: { columns: bodyMeasurementsColumns, dataField: "waistRatio", section: MEASUREMENT_SECTIONS.BODY_MEASUREMENTS },
  systolicBp: { columns: bloodPressureColumns, dataField: "systolic", section: MEASUREMENT_SECTIONS.BLOOD_PRESSURE },
  diastolicBp: { columns: bloodPressureColumns, dataField: "diastolic", section: MEASUREMENT_SECTIONS.BLOOD_PRESSURE },
  heartRate: { columns: bloodPressureColumns, dataField: "heartRate", section: MEASUREMENT_SECTIONS.BLOOD_PRESSURE },
  totalCholesterol: { columns: lipidProfileColumns, dataField: "totalCholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  hdlCholesterol: { columns: lipidProfileColumns, dataField: "hdlCholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  triglycerides: { columns: lipidProfileColumns, dataField: "triglycerides", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  ldlCholesterol: { columns: lipidProfileColumns, dataField: "ldlCholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  totalHdlRatio: { columns: lipidProfileColumns, dataField: "totalHdlRatio", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  nonHdlCholesterol: { columns: lipidProfileColumns, dataField: "nonHdlCholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  heartAge: { columns: lipidProfileColumns, dataField: "heartAge", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  cvdRisk: { columns: lipidProfileColumns, dataField: "cvdRisk", section: MEASUREMENT_SECTIONS.LIPID_PROFILE },
  rbg: { columns: glucoseColumns, dataField: "glucose", section: MEASUREMENT_SECTIONS.GLUCOSE },
};

const getRiskField = (riskAssessment, key) =>
  riskAssessment?.[key] ?? { value: null, result: null };

const isEmptyValue = (value) =>
  value === null || value === undefined || value === "" || value === "-";

const MISSING_FIELD_DEFS = [
  { label: "Height", section: MEASUREMENT_SECTIONS.BODY_MEASUREMENTS, getValue: (_ra, raw) => raw.height },
  { label: "Weight", section: MEASUREMENT_SECTIONS.BODY_MEASUREMENTS, getValue: (_ra, raw) => raw.weight },
  { label: "Waist:Height Ratio", section: MEASUREMENT_SECTIONS.BODY_MEASUREMENTS, getValue: (ra) => getRiskField(ra, "waistHeightRatio").value },
  { label: "BMI", section: MEASUREMENT_SECTIONS.BODY_MEASUREMENTS, getValue: (ra) => getRiskField(ra, "bmi").value },
  { label: "Systolic BP", section: MEASUREMENT_SECTIONS.BLOOD_PRESSURE, getValue: (ra) => getRiskField(ra, "systolicBp").value },
  { label: "Diastolic BP", section: MEASUREMENT_SECTIONS.BLOOD_PRESSURE, getValue: (ra) => getRiskField(ra, "diastolicBp").value },
  { label: "Heart rate", section: MEASUREMENT_SECTIONS.BLOOD_PRESSURE, getValue: (ra) => getRiskField(ra, "heartRate").value },
  { label: "Total Cholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "totalCholesterol").value },
  { label: "HDL Cholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "hdlCholesterol").value },
  { label: "Triglycerides", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "triglycerides").value },
  { label: "LDL Cholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "ldlCholesterol").value },
  { label: "TC:HDL Ratio", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "totalHdlRatio").value },
  { label: "Non-HDL Cholesterol", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "nonHdlCholesterol").value },
  { label: "Heart age", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "heartAge").value },
  { label: "10-year CVD risk", section: MEASUREMENT_SECTIONS.LIPID_PROFILE, getValue: (ra) => getRiskField(ra, "cvdRisk").value },
  { label: "Glucose", section: MEASUREMENT_SECTIONS.GLUCOSE, getValue: (ra) => getRiskField(ra, "rbg").value },
];

const getMissingFields = (riskAssessment, rawMeasurements, enabledSections) =>
  MISSING_FIELD_DEFS
    .filter(({ section }) => enabledSections.includes(section))
    .filter(({ getValue }) => isEmptyValue(getValue(riskAssessment, rawMeasurements)))
    .map(({ label }) => label);

const getRiskFieldRangeError = (riskAssessment, apiKey) => {
  const mapping = FIELD_VALIDATION_MAP[apiKey];
  if (!mapping) return null;

  const column = mapping.columns.find((c) => c.dataField === mapping.dataField);
  const { value } = getRiskField(riskAssessment, apiKey);
  return getRangeError(value, column?.min, column?.max);
};

const getOutOfRangeParameters = (riskAssessment, enabledSections) => {
  const outOfRange = [];

  Object.keys(FIELD_VALIDATION_MAP).forEach((apiKey) => {
    const mapping = FIELD_VALIDATION_MAP[apiKey];
    if (!enabledSections.includes(mapping.section)) return;

    const column = mapping.columns.find((c) => c.dataField === mapping.dataField);
    const field = getRiskField(riskAssessment, apiKey);
    const value = field.value;
    
    if (isEmptyValue(value)) return;
    
    const error = getRangeError(value, column?.min, column?.max);
    if (error) {
      const label = PARAMETER_LABEL_MAP[apiKey] || apiKey;
      outOfRange.push(label);
    }
  });

  return outOfRange;
};

const SPLIT_TABLE_COLUMNS = [
  { dataField: "parameter", text: "Parameter" },
  { dataField: "reading", text: "Reading" },
  { dataField: "unit", text: "Unit" },
  {
    dataField: "classification",
    text: "Classification",
    render: renderClassification,
  },
];

const BLOOD_PRESSURE_WIDE_COLUMNS = [
  { dataField: "reading", text: "Reading" },
  {
    dataField: "systolic",
    text: (
      <>
        Systolic <br />
        <span>(mmHg)</span>
      </>
    ),
  },
  {
    dataField: "diastolic",
    text: (
      <>
        Diastolic <br />
        <span>(mmHg)</span>
      </>
    ),
  },
  {
    dataField: "classification",
    text: "Classification",
    render: renderClassification,
  },
  {
    dataField: "heartRate",
    text: (
      <>
        Heart rate <br />
        <span>(bpm)</span>
      </>
    ),
  },
];

const warningIcon = (
  <img
    src={WarningIcon}
    className="row-warning-icon"
    alt="value outside expected range"
  />
);

const buildSplitParameterRow = ({
  riskAssessment,
  apiKey,
  label,
  unit,
  isInfo = false,
}) => {
  const field = getRiskField(riskAssessment, apiKey);
  const hasWarning = Boolean(getRiskFieldRangeError(riskAssessment, apiKey));

  return {
    parameter: (
      <span>
        {label}
        {isInfo && <img src={InfoDefaultIcon} alt="" />}
        {hasWarning && warningIcon}
      </span>
    ),
    reading: field.value ?? "-",
    unit: unit || "-",
    classification: field.result || "-",
  };
};

const buildBodyMeasurementsRows = (riskAssessment, rawMeasurements = {}) => {
  return addIds([
    {
      parameter: "Height",
      reading: rawMeasurements.height || "-",
      unit: "cm",
      classification: "-",
    },
    {
      parameter: "Weight",
      reading: rawMeasurements.weight || "-",
      unit: "kg",
      classification: "-",
    },
    buildSplitParameterRow({
      riskAssessment,
      apiKey: "waistHeightRatio",
      label: "Waist:Height Ratio",
      unit: null,
    }),
    buildSplitParameterRow({riskAssessment, apiKey: "bmi", label: "BMI", unit: "kg/m2"}),
  ]);
};

const buildBloodPressureRows = (riskAssessment) => {
  const systolic = getRiskField(riskAssessment, "systolicBp");
  const diastolic = getRiskField(riskAssessment, "diastolicBp");
  const heartRate = getRiskField(riskAssessment, "heartRate");

  const systolicError = getRiskFieldRangeError(riskAssessment, "systolicBp");
  const diastolicError = getRiskFieldRangeError(riskAssessment, "diastolicBp");
  const heartRateError = getRiskFieldRangeError(riskAssessment, "heartRate");
  const hasAnyWarning = Boolean(
    systolicError || diastolicError || heartRateError,
  );

  const classification = systolic.result || diastolic.result || "-";

  return addIds([
    {
      reading: (
        <span>
          Clinic blood
          <br /> pressure
          {hasAnyWarning && warningIcon}
        </span>
      ),
      systolic: systolic.value ?? "-",
      diastolic: diastolic.value ?? "-",
      heartRate: heartRate.value ?? "-",
      classification,
    },
  ]);
};

const getHeartRhythmDisplay = (heartRhythmIrregular) => {
  if (heartRhythmIrregular === true) {
    return <span style={{ color: "#D8221F" }}>Yes</span>;
  }
  if (heartRhythmIrregular === false) return "No";
  return "-";
};

const LIPID_PROFILE_FIELD_DEFS = [
  { apiKey: "totalCholesterol", label: "Total Cholesterol", unit: "mmol/L" },
  { apiKey: "hdlCholesterol", label: "HDL Cholesterol", unit: "mmol/L" },
  {
    apiKey: "triglycerides",
    label: "Triglycerides",
    unit: "mmol/L",
    isInfo: true,
  },
  { apiKey: "ldlCholesterol", label: "LDL Cholesterol", unit: "mmol/L" },
  { apiKey: "totalHdlRatio", label: "TC:HDL Ratio", unit: "", isInfo: true },
  { apiKey: "nonHdlCholesterol", label: "Non-HDL Cholesterol", unit: "mmol/L" },
  { apiKey: "heartAge", label: "Heart age", unit: "years" },
  { apiKey: "cvdRisk", label: "10-year CVD risk", unit: "%" },
];

const buildLipidProfileRows = (riskAssessment) =>
  addIds(
    LIPID_PROFILE_FIELD_DEFS.map(({ apiKey, label, unit, isInfo }) =>
      buildSplitParameterRow({riskAssessment, apiKey, label, unit, isInfo}),
    ),
  );

const buildGlucoseRows = (riskAssessment) =>
  addIds([buildSplitParameterRow({riskAssessment, apiKey: "rbg", label: "Glucose", unit: "mmol/L"})]);

const getPositiveScreeningQuestions = (questions, answers) =>
  questions.filter((question) => answers[question.name] === true);

const ResultSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { callApi, serverError, tokenError } = useApi();
  const auth = useAuth();

  const bookingId = location.state?.bookingId;
  const serviceName = location.state?.serviceName;
  const patientSex = location.state?.patientSex;
  const serviceType = location.state?.serviceType;

  const {
    draft,
    isLoaded: isDraftLoaded,
    saveSlice,
  } = useConsultationDraft(bookingId, serviceName, patientSex, serviceType);

  const enabledSections = getEnabledSections(serviceName);
  const isSectionEnabled = (section) => enabledSections.includes(section);

  const [riskAssessment, setRiskAssessment] = useState({});
  const [isRiskAssessmentLoaded, setIsRiskAssessmentLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patientDetails, setPatientDetails] = useState(
    location.state?.displayUserInfo ?? auth.displayUserInfo,
  );

  useEffect(() => {
    const fetchPatientDetails = async () => {
      try {
      const response = await callApi(
        null,
        `${patientDetailsGet}?booking_id=${bookingId}&service_name=${serviceName}`,
        "GET",
      );
      const formattedData = {
        ...response?.data,
        dob: response?.data?.dob ? formatDtAge(response.data.dob, true) : null,
        sex: response?.data?.sex
          ? response?.data?.sex.charAt(0).toUpperCase() +
            response?.data?.sex.slice(1)
          : null,
      };
      setPatientDetails(
        formattedData ??
          location.state?.displayUserInfo ??
          auth.displayUserInfo,
      );
      } catch (error) {
        setPatientDetails(
          location.state?.displayUserInfo ?? auth.displayUserInfo,
        );
      }
    };

    fetchPatientDetails();
  }, [auth?.featureFlag, navigate]);

  useEffect(() => {
    if (
      !ALLOWED_STAGES.includes(sessionStorage.getItem("allowedStage")) &&
      auth?.featureFlag
    ) {
      navigate("/patient-record");
    }
  }, [auth?.featureFlag, navigate]);

  useEffect(() => {
    if (!bookingId || !serviceName) {
      setIsRiskAssessmentLoaded(true);
      return;
    }

    let cancelled = false;

    const fetchResults = async () => {
      try {
        const params = new URLSearchParams({
          booking_id: bookingId,
          service_name: serviceName,
        });

        const response = await callApi(
          null,
          `${resultOutcomeGet}?${params}`,
          "GET",
        );
        if (!cancelled) {
          setRiskAssessment(response?.data || {});
        }
      } catch (error) {
        // fetch results failed. User remains on the current page.
      } finally {
        if (!cancelled) {
          setIsRiskAssessmentLoaded(true);
        }
      }
    };

    fetchResults();

    return () => {
      cancelled = true;
    };
  }, [bookingId, serviceName]);

  const isLoaded = isDraftLoaded && isRiskAssessmentLoaded;

  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [isConfirmSubmitModalOpen, setIsConfirmSubmitModalOpen] =
    useState(false);
  const [notes, setNotes] = useState("");

  const hasHydrated = useRef(false);
  useEffect(() => {
    if (!isLoaded || hasHydrated.current) return;
    setNotes(draft?.notes || "");
    hasHydrated.current = true;
  }, [isLoaded, draft]);

  const skipNextSave = useRef(true);
  useEffect(() => {
    if (!isLoaded) return;

    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }

    saveSlice({ notes });
  }, [notes, isLoaded]);

  const rawMeasurements = draft?.clinical_measurements?.body_measurements || {};
  const missingFields = getMissingFields(riskAssessment, rawMeasurements, enabledSections);
  const outOfRangeParameters = getOutOfRangeParameters(riskAssessment, enabledSections);
  const heartRhythmIrregular =
    draft?.clinical_measurements?.heart_rhythm_irregular;

  const screeningQuestions = getScreeningQuestions(serviceName);
  const screeningAnswers = draft?.screening_questions?.answers || {};
  const positiveScreeningQuestions = getPositiveScreeningQuestions(
    screeningQuestions,
    screeningAnswers,
  );

  const handleExitConsultation = () => {
    setIsExitModalOpen(true);
  };

  const handleCloseExitModal = () => {
    setIsExitModalOpen(false);
  };

  const handleConfirmExit = () => {
    setIsExitModalOpen(false);
  };

  const handleEditClinicalScreening = () => {
    navigate(CLINICAL_SCREENING_EDIT_ROUTE, {
      state: { ...location.state, isEditMode: true },
    });
  };

  const handleEditClinicalMeasurements = () => {
    navigate(CLINICAL_MEASUREMENTS_EDIT_ROUTE, {
      state: { ...location.state, isEditMode: true },
    });
  };

  const handleOpenConfirmSubmitModal = () => {
    if (!bookingId || !serviceName || isSubmitting) return;
    setIsConfirmSubmitModalOpen(true);
  };

  const handleCloseConfirmSubmitModal = () => {
    if (isSubmitting) return;
    setIsConfirmSubmitModalOpen(false);
  };
  useEffect(() => {
    if (serverError || tokenError) {
      auth?.showToast(
        "Error",
        tokenError || serverError,
        "../assets/mhc.png",
        {icon: <XCircleFill />,
        className: auth?.featureFlag && "vh-toaster"}
      );
    }
  }, [serverError, tokenError]);

  const handleConfirmAndSubmit = async () => {
    if (!bookingId || !serviceName || isSubmitting) return;
    setIsSubmitting(true);
      const payload = {
        booking_id: bookingId,
        service_name: serviceName,
        out_of_range: outOfRangeParameters,
        missing: missingFields,
      };
      const response = await callApi(payload, consultationSubmitPost, "POST");
      if (response?.success) {
      setIsConfirmSubmitModalOpen(false);
      navigate("/result-outcome", {
        state: {
          bookingId,
          serviceType,
          serviceName,
          patientSex,
          patientDetails,
          fromValidSource: true,
          isOutcomePage: true,
        },
        replace: true,
      });
      }
      setIsSubmitting(false);
  };
  if (!isLoaded) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" className="my-custom-spinner" />
      </div>
    );
  }

  return (
    <div className="result-summary-page">
      <LoggedInLayout isVhlogin={true} vitalityLayout={true}>
        <ExitConsultationButton onClick={handleExitConsultation} />
        <ExitModal
          isOpen={isExitModalOpen}
          onClose={handleCloseExitModal}
          onConfirm={handleConfirmExit}
          bookingId={bookingId}
          notes={notes}
          onNotesChange={setNotes}
        />
        <ConfirmSubmissionModal
          isOpen={isConfirmSubmitModalOpen}
          onClose={handleCloseConfirmSubmitModal}
          onConfirm={handleConfirmAndSubmit}
          isSubmitting={isSubmitting}
          missingFields={missingFields}
        />
        <PageLayout
          heading="Results summary"
          className="clinical-measurement"
          isBack={true}
        >
          <div className="d-flex flex-column gap-3">
            <div className="outcome-card">
              {/* Patient details */}
              <DetailCard
                heading="Patient details"
                data={{
                  ...patientDetails,
                  booking_id: bookingId,
                }}
                fields={vitalitySearchFormFields}
              />

              {/* Clinical screening */}
              <div className="outcome-card__header">
                <h1>Eligibility</h1>
                <button
                  type="button"
                  className="edit-risk-profile-link"
                  onClick={handleEditClinicalScreening}
                >
                  <img src={EditIcon} alt="edit-icon" />
                  Edit
                </button>
              </div>
              <hr />
              <div className="clinical-screening">
                <p className="clinical-screening__title">Clinical screening</p>
                {positiveScreeningQuestions.length === 0 ? (
                  <div className="clinical-screening__row">
                    <span>-</span>
                  </div>
                ) : (
                  positiveScreeningQuestions.map((question) => (
                    <div
                      className="clinical-screening__row row"
                      key={question.name}
                    >
                      <span className="col-md-9 col-sm-10">
                        {question.label}
                      </span>
                      <span className="col-md-3 col-sm-2">Yes</span>
                    </div>
                  ))
                )}
              </div>

              {/* Clinical measurements */}
              <div className="outcome-card__header">
                <h1>Clinical measurements</h1>
                <button
                  type="button"
                  className="edit-risk-profile-link"
                  onClick={handleEditClinicalMeasurements}
                >
                  <img src={EditIcon} alt="edit-icon" />
                  Edit
                </button>
              </div>
              <hr />
              {isSectionEnabled(MEASUREMENT_SECTIONS.BODY_MEASUREMENTS) && (
              <div className="clinical-table-block">
                <p className="clinical-table-block__title">Body measurements</p>
                <VitalityDuplicatedTable
                  data={buildBodyMeasurementsRows(
                    riskAssessment,
                    rawMeasurements,
                  )}
                  columns={SPLIT_TABLE_COLUMNS}
                  pagination={false}
                  onFieldChange={null}
                  errors={{}}
                />
              </div>)}
              
              {isSectionEnabled(MEASUREMENT_SECTIONS.BLOOD_PRESSURE) && (
              <div className="clinical-table-block">
                <p className="clinical-table-block__title">
                  Blood pressure &amp; pulse
                </p>
                <VitalityDuplicatedTable
                  data={buildBloodPressureRows(riskAssessment)}
                  columns={BLOOD_PRESSURE_WIDE_COLUMNS}
                  pagination={false}
                  onFieldChange={null}
                  errors={{}}
                />
                <p className="clinical-table-block__footnote">
                  Irregular pulse detected:{" "}
                  <strong>{getHeartRhythmDisplay(heartRhythmIrregular)}</strong>
                </p>
              </div>)}

              {isSectionEnabled(MEASUREMENT_SECTIONS.LIPID_PROFILE) && (
                <>
              <div className="clinical-table-block">
                <p className="clinical-table-block__title">
                  Lipid profile (Cholesterol)
                </p>
                <VitalityDuplicatedTable
                  data={buildLipidProfileRows(riskAssessment)}
                  columns={SPLIT_TABLE_COLUMNS}
                  pagination={false}
                  onFieldChange={null}
                  errors={{}}
                />
              </div>

              <VHConfirmationPopup
                infoText={
                  <span>
                    Customer-facing reporting categories have been intentionally
                    simplified to support understanding and engagement.
                    Healthcare professionals should use the result values and
                    overall clinical assessment, alongside the clinical training
                    and Biometrics SOP Appendix, when determining the
                    appropriate advice and referral pathways.
                  </span>
                }
                showIcon={false}
              /></>)}
              {isSectionEnabled(MEASUREMENT_SECTIONS.GLUCOSE) && (
              <div className="clinical-table-block">
                <p className="clinical-table-block__title">
                  Glucose (random, non-fasting)
                </p>
                <VitalityDuplicatedTable
                  data={buildGlucoseRows(riskAssessment)}
                  columns={SPLIT_TABLE_COLUMNS}
                  pagination={false}
                  onFieldChange={null}
                  errors={{}}
                />
              </div>)}
            </div>
          </div>

          {/* Notes */}
          <Notes
            value={notes}
            onChange={setNotes}
            disabled={false}
            className="mt-4"
            label="Notes"
          />

          <div className="button-layout">
            <Button
              className="mx-2 my-2"
              label="Confirm & Submit"
              onClick={handleOpenConfirmSubmitModal}
              size="large"
              type="button"
              variant="primary"
              disabled={isSubmitting}
            />
          </div>
        </PageLayout>
      </LoggedInLayout>
    </div>
  );
};

export default ResultSummary;
